import { useEffect, useRef } from 'react';
import { openLanding, PLUSZ_PATH } from '../lib/openLanding';
import { track } from '../lib/track';
import ModuleResources from './ModuleResources';
import GyakorloLink from './gyakorlo/GyakorloLink';
import AlakzatLink from './alakzat/AlakzatLink';

/* ══════════════════════════════════════════════════════════════════════════
   ZÁRT MODUL (Plusz 2. kör, D.2)
   ─────────────────────────────────────────────────────────────────────────
   A modul címe, a leckék címe és hossza látszik (a course_outline RPC adja,
   tartalom nélkül), lakattal, és a szintnek megfelelő gomb:
     tag   → „TAG-gal nyílik” (a landing TAG-aktiváló ablaka), mellette „vagy Plusszal”
     plusz → „Plusszal nyílik” (a landing /plusz#arak, Plusz 3. kör)
     pro   → „PRO-val nyílik”
   Mérés: module_locked_view {modul, szint} — csak amikor a kártya a képernyőre
   ér, munkamenetenként modulonként egyszer (sessionStorage; Plusz 3. kör, F.2);
   module_unlock_click {cel}; a Plusz-gomboknál plusz_cta_click {hely} is.
   ══════════════════════════════════════════════════════════════════════════ */

const CYAN = '#00D2C8';
const PURPLE = '#A97BFF';
const SEEN_KEY = 'w3k_module_locked_seen';

const seenInMemory = new Set(); // ha a sessionStorage nem érhető el (privát mód)

/* Munkamenetenként modulonként egyszer (a lap újratöltése után sem ismétel). */
function markSeen(id) {
  try {
    const list = JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]');
    if (list.includes(id)) return false;
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...list, id].slice(-200)));
    return true;
  } catch {
    if (seenInMemory.has(id)) return false;
    seenInMemory.add(id);
    return true;
  }
}

export const LEVEL_TEXT = { tag: 'TAG-gal nyílik', plusz: 'Plusszal nyílik', pro: 'PRO-val nyílik', og: 'OG-hozzáféréssel nyílik' };

export function unlockTarget(level) {
  if (level === 'tag') return '/dashboard?tag=aktivalas';
  if (level === 'plusz') return PLUSZ_PATH;
  return '/pro';
}

export function UnlockButtons({ level, session, compact = false, hely = 'egyetem_modul' }) {
  const go = (cel, path) => {
    track(session, 'module_unlock_click', { cel });
    if (cel === 'plusz') track(session, 'plusz_cta_click', { hely: level === 'tag' ? `${hely}_vagy_plusszal` : hely });
    openLanding(path);
  };
  const primary = {
    border: 'none', borderRadius: 10, padding: compact ? '9px 14px' : '11px 16px', fontWeight: 700, fontSize: 14,
    cursor: 'pointer', fontFamily: 'inherit', background: level === 'pro' ? PURPLE : CYAN, color: '#0a0f1e',
  };
  const link = { background: 'none', border: 'none', color: CYAN, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 2px', textDecoration: 'underline' };
  if (level === 'og') return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <button type="button" style={primary} onClick={() => go(level === 'tag' ? 'tag' : level, unlockTarget(level))}>{LEVEL_TEXT[level]}</button>
      {level === 'tag' && <button type="button" style={link} onClick={() => go('plusz', PLUSZ_PATH)}>vagy Plusszal</button>}
    </div>
  );
}

export default function LockedModule({ module, session, resources }) {
  const level = module.lessons[0]?.required_level || 'plusz';
  const n = module.lessons.length;

  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fire = () => { if (markSeen(module.id)) track(session, 'module_locked_view', { modul: module.title, szint: level }); };
    if (!('IntersectionObserver' in window)) { fire(); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { fire(); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [module.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref} style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span aria-hidden="true">🔒</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{module.displayIndex}. {module.title}</span>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {n}{' '}lecke · {LEVEL_TEXT[level]}
        </div>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '0.5px dashed rgba(255,255,255,0.12)', borderRadius: 12, padding: 14, display: 'grid', gap: 12 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {module.lessons.map(l => (
            <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.5)', minWidth: 0 }}>
              <span aria-hidden="true" style={{ opacity: 0.6, flexShrink: 0 }}>🔒</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</span>
              {l.duration_minutes ? <span style={{ flexShrink: 0, color: 'rgba(255,255,255,0.3)' }}>{l.duration_minutes}{' '}perc</span> : null}
            </li>
          ))}
        </ul>
        <UnlockButtons level={level} session={session} />
        {/* A segédletek zárt modulnál is látszanak: az ingyenes puska FREE-nek is jár. */}
        <ModuleResources items={resources} session={session} />
        <GyakorloLink moduleId={module.id} />
        <AlakzatLink moduleId={module.id} />
      </div>
    </div>
  );
}
